import { Observable } from 'rxjs';

import config from 'config';

import { isTokenExpired } from './auth/isTokenExpired';

interface ResponseError<ErrorResponseBody = unknown> extends Error {
  response?: Response;
  responseBody?: ErrorResponseBody;
}

interface StringMap {
  [key: string]: string | unknown;
}

class Api {
  endpoint: string;

  // @NOTE-DP: a flag to indicate that the log out process is ongoing. We block all of the outgoing requests until the logout process is complete.
  LOGGING_OUT = false;

  setAuthorization: ((token: string) => void) | null = null;

  handleFailedAuth: () => void = () => {};

  defaultHeaders: Record<string, string> = {};

  constructor(baseEndpoint?: string) {
    this.endpoint = (baseEndpoint || config.apiUrl) as string;
  }

  request<ResponseBody = unknown, ErrorResponseBody = unknown>(
    path: string,
    additionalOptions?: StringMap,
    data?: StringMap,
    additionalHeaders?: StringMap
  ): Observable<ResponseBody> {
    if (this.defaultHeaders.Authorization) {
      const token = this.defaultHeaders.Authorization.split(' ')[1];

      if (token && isTokenExpired(token)) {
        // Commence the log out
        this.handleFailedAuth();
        // Return an observable that doesn't emit anything
        // The reason why we prefer `never` to `empty`, is that we want to be sure it just hangs without completing, to be absolutely sure that no side-effects would be triggered in the subscribing code
        return Observable.never();
      }
    }

    if (this.LOGGING_OUT) {
      // If we are already in the process of being logged out, lock other requests as well and make them hang
      return Observable.never();
    }

    const options: StringMap = { ...additionalOptions };
    if (data) {
      options.body = JSON.stringify(data);
    }
    const mergedHeader = { ...this.defaultHeaders, ...additionalHeaders };
    return Observable.from(
      fetch(`${this.endpoint}/${path}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'x-deed-api-version': '4',
          ...mergedHeader,
        },
        mode: 'cors',
        credentials: 'include',
      }).then(async (response) => {
        if (response.status >= 200 && response.status < 300) {
          // Update token. Currently used when changing (not resetting) the password
          const token = response.headers.get('Set-Authorization');
          if (token && this.setAuthorization) {
            this.setAuthorization(token);
          }

          if (response.status === 204 || response.status === 205) {
            // Not empty
            return null;
          }
          return response.json();
        }

        if (response.status === 401) {
          this.handleFailedAuth();
        }

        const statusTexts: { [key: number]: string } = {
          400: 'Bad Request',
          401: 'Unauthorized',
          403: 'Forbidden',
          404: 'Not Found',
          408: 'Request Timeout',
          422: 'Unprocessable Entity',
          500: 'Internal Server Error',
          503: 'Service Unavailable',
          504: 'Gateway Timeout',
        };

        const error: ResponseError<ErrorResponseBody> = new Error(
          response.statusText || statusTexts[response.status]
        );
        error.response = response;

        try {
          error.responseBody = await response.json();
        } catch (bodyParseError) {
          console.warn(bodyParseError, `Couldn't parse error response, continuing anyways...`);
        }

        throw error;
      })
    );
  }

  get<T = unknown>(
    path: string,
    options?: StringMap,
    additionalHeaders?: StringMap
  ): Observable<T> {
    return this.request(path, options, undefined, additionalHeaders);
  }

  post<T = unknown>(
    path: string,
    data?: StringMap,
    options?: StringMap,
    additionalHeaders?: StringMap
  ): Observable<T> {
    return this.request(path, { ...options, method: 'POST' }, data, additionalHeaders);
  }

  put<T = unknown>(
    path: string,
    data: StringMap,
    options?: StringMap,
    additionalHeaders?: StringMap
  ): Observable<T> {
    return this.request(path, { ...options, method: 'PUT' }, data, additionalHeaders);
  }

  patch<T = unknown>(
    path: string,
    data: StringMap,
    options?: StringMap,
    additionalHeaders?: StringMap
  ): Observable<T> {
    return this.request(path, { ...options, method: 'PATCH' }, data, additionalHeaders);
  }

  delete<T = unknown>(
    path: string,
    options?: StringMap,
    additionalHeaders?: StringMap
  ): Observable<T> {
    return this.request(path, { ...options, method: 'DELETE' }, undefined, additionalHeaders);
  }
}

export const ApiInstance = Api;
export default new Api();
